import React, { useEffect, useMemo, useState } from "react";
import {
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CRow,
  CSpinner,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
  CBadge,
  CFormInput,
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CButton,
  CFormSelect,
} from "@coreui/react";
import { CChartBar, CChartDoughnut } from "@coreui/react-chartjs";
import SalesPicker from "src/components/SalesPicker";
import ls from "local-storage";
import { getAll as getAllStores, getUplines } from "src/services/Stores";
import DataTable from "src/components/DataTable";
import { useNavigate } from "react-router-dom";

function StoresPerUpline() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stores, setStores] = useState([]);
  const [search, setSearch] = useState("");
  const [showModalType, setShowModalType] = useState(false);
  const [showModalStore, setShowModalStore] = useState(false);
  const [selectedUplineKey, setSelectedUplineKey] = useState(null);
  const [uplinesMap, setUplinesMap] = useState({}); // id -> name
  
  // New filter states
  const [visitFilter, setVisitFilter] = useState("all"); // "all", "visited", "not_visited"
  const [typeFilter, setTypeFilter] = useState("all"); // "all" or specific type
  const [availableTypes, setAvailableTypes] = useState([]);

  const getFilterParams = () => {
    const smd = ls.get("smd");
    const prov = ls.get("prov");
    const city = ls.get("city");

    return {
      smdId: smd?.id && smd.id !== "999" && smd.value !== "all" ? smd.id : "0",
      provinceId: prov?.id && prov.id !== "999" && prov.value !== "all" ? prov.id : "0",
      cityId: city?.id && city.id !== "999" && city.value !== "all" ? city.id : "0",
    };
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const filters = getFilterParams();
      const [res, uplines] = await Promise.all([
        getAllStores(filters),
        getUplines(),
      ]);
      setStores(res?.stores || []);

      const list = (uplines?.data || uplines?.uplines || uplines) || [];
      const mapped = {};
      for (const u of list) {
        const idCandidate = u?.id ?? u?.upline_id ?? u?.uplineId ?? u?.upline_code;
        if (idCandidate === undefined || idCandidate === null) continue;
        const id = String(idCandidate);
        const name = u?.name ?? u?.upline_name ?? u?.uplineName ?? id;
        mapped[id] = name;
      }
      setUplinesMap(mapped);

      // Extract available types from stores
      const types = [...new Set(stores.map(s => s?.type || "-").filter(t => t !== "-"))];
      setAvailableTypes(types);
    } catch (error) {
      console.error("Error fetching stores for upline analytics:", error);
      setStores([]);
      setUplinesMap({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const groupedByUpline = useMemo(() => {
    const groups = {};
    for (const s of stores) {
      // Apply visit filter
      if (visitFilter === "visited" && !s?.last_visited) continue;
      if (visitFilter === "not_visited" && s?.last_visited) continue;
      
      // Apply type filter
      if (typeFilter !== "all" && s?.type !== typeFilter) continue;

      const idRaw = s?.upline_name; // Note: API returns ID in this field
      const key = idRaw ? String(idRaw) : "no_upline";
      const displayName = idRaw ? (uplinesMap[String(idRaw)] || `Upline ${idRaw}`) : "Tanpa Upline";
      if (!groups[key]) {
        groups[key] = {
          uplineId: idRaw || null,
          upline: displayName,
          total: 0,
          visited: 0,
          notVisited: 0,
          typeCount: {},
        };
      }
      groups[key].total += 1;
      if (s?.last_visited) groups[key].visited += 1;
      else groups[key].notVisited += 1;
      const t = s?.type || "-";
      groups[key].typeCount[t] = (groups[key].typeCount[t] || 0) + 1;
    }
    return groups;
  }, [stores, uplinesMap, visitFilter, typeFilter]);

  const sortedUplineKeys = useMemo(() => {
    return Object.keys(groupedByUpline).sort(
      (a, b) => groupedByUpline[b].total - groupedByUpline[a].total
    );
  }, [groupedByUpline]);

  const filteredUplineKeys = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return sortedUplineKeys;
    return sortedUplineKeys.filter((id) => groupedByUpline[id]?.upline?.toLowerCase().includes(term));
  }, [sortedUplineKeys, search, groupedByUpline]);

  const TOP_N = 1000;
  const barChartLabels = useMemo(() => {
    if (!filteredUplineKeys.length) return [];
    const topIds = filteredUplineKeys.slice(0, TOP_N);
    const hasOthers = filteredUplineKeys.length > TOP_N;
    const labels = topIds.map((id) => groupedByUpline[id]?.upline || id);
    return hasOthers ? [...labels, "Lainnya"] : labels;
  }, [filteredUplineKeys, groupedByUpline]);

  const barChartData = useMemo(() => {
    if (!filteredUplineKeys.length) return [];
    const topIds = filteredUplineKeys.slice(0, TOP_N);
    const topValues = topIds.map((id) => groupedByUpline[id].total);
    if (filteredUplineKeys.length > TOP_N) {
      const others = filteredUplineKeys.slice(TOP_N);
      const othersSum = others.reduce((sum, id) => sum + groupedByUpline[id].total, 0);
      return [...topValues, othersSum];
    }
    return topValues;
  }, [filteredUplineKeys, groupedByUpline]);

  const topUplineIds = useMemo(() => filteredUplineKeys.slice(0, TOP_N), [filteredUplineKeys]);

  const handleBarClick = (index) => {
    if (index == null) return;
    if (index < topUplineIds.length) {
      const id = topUplineIds[index];
      navigate(`/stores?upline_name=${id}`)
    }
  };

  const handleDonutClick = (upline, tipe, visitStatus) => {
    const visitParam = visitStatus === 'visited' ? 'visitStatus=Terkunjungi' : 'visitStatus=Belum+Terkunjungi';
    navigate(`/stores?type=${tipe}&upline_name=${upline}&${visitParam}`)
  }

  // New handlers for filter clicks
  const handleVisitFilterClick = (filter) => {
    setVisitFilter(filter);
  };

  const handleTypeFilterClick = (type) => {
    setTypeFilter(type);
  };

  const clearFilters = () => {
    setVisitFilter("all");
    setTypeFilter("all");
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "80vh" }}>
        <CSpinner color="primary" size="lg" />
      </div>
    );
  }

  return (
    <div>
      <SalesPicker onFilterChange={fetchData} />
      
      <CRow className="mt-3">
        <CCol lg={12}>
          <CCard className="mb-4">
            <CCardHeader>
              <h4>Jumlah Toko per Upline</h4>
            </CCardHeader>
            <CCardBody>
              <CChartBar
                data={{
                  labels: barChartLabels,
                  datasets: [
                    {
                      label: "Total Toko",
                      backgroundColor: "#36A2EB",
                      data: barChartData,
                    },
                  ],
                }}
                options={{
                  indexAxis: "x",
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { position: "bottom" },
                  },
                  scales: {
                    x: { beginAtZero: true },
                  },
                  onClick: (evt, elements) => {
                    if (elements && elements.length > 0) {
                      const idx = elements[0].index;
                      handleBarClick(idx);
                    }
                  },
                }}
                style={{ minHeight: 300, maxHeight: 400 }}
              />
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      <CRow>
        <CCol lg={12}>
          <CCard className="mb-4">
            <CCardHeader>
              <CRow className="align-items-center">
                <CCol xs={12} className="mb-2">
                  <h4 className="mb-0">Ringkasan per Upline</h4>
                </CCol>
              </CRow>
              <CRow className="g-2">
                <CCol xs={12} sm={6} lg={4}>
                  <CFormInput
                    type="text"
                    placeholder="Cari upline..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    size="sm"
                  />
                </CCol>
                <CCol xs={6} sm={3} lg={2}>
                  <CFormSelect
                    value={visitFilter}
                    onChange={(e) => setVisitFilter(e.target.value)}
                    size="sm"
                  >
                    <option value="all">Semua</option>
                    <option value="visited">Terkunjungi</option>
                    <option value="not_visited">Belum</option>
                  </CFormSelect>
                </CCol>
                <CCol xs={6} sm={3} lg={2}>
                  <CFormSelect
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                    size="sm"
                  >
                    <option value="all">Semua Tipe</option>
                    <option value="A">Tipe A</option>
                    <option value="B">Tipe B</option>
                    <option value="C">Tipe C</option>
                  </CFormSelect>
                </CCol>
                <CCol xs={12} sm={6} lg={2}>
                  <CButton 
                    color="secondary" 
                    variant="outline" 
                    size="sm" 
                    onClick={clearFilters}
                    className="w-100"
                  >
                    Reset Filter
                  </CButton>
                </CCol>
              </CRow>
            </CCardHeader>
            <CCardBody>
              <div style={{ maxHeight: 420, overflowY: "auto" }}>
                <CTable hover responsive>
                <CTableHead>
                  <CTableRow>
                    <CTableHeaderCell>Upline</CTableHeaderCell>
                    <CTableHeaderCell className="text-center">Total</CTableHeaderCell>
                    <CTableHeaderCell className="text-center">
                      <span 
                        style={{ 
                          cursor: "pointer", 
                          textDecoration: visitFilter === "visited" ? "underline" : "none",
                          fontWeight: visitFilter === "visited" ? "bold" : "normal"
                        }}
                        onClick={() => handleVisitFilterClick("visited")}
                      >
                        Terkunjungi
                      </span>
                    </CTableHeaderCell>
                    <CTableHeaderCell className="text-center">
                      <span 
                        style={{ 
                          cursor: "pointer", 
                          textDecoration: visitFilter === "not_visited" ? "underline" : "none",
                          fontWeight: visitFilter === "not_visited" ? "bold" : "normal"
                        }}
                        onClick={() => handleVisitFilterClick("not_visited")}
                      >
                        Belum Terkunjungi
                      </span>
                    </CTableHeaderCell>
                    <CTableHeaderCell>Tipe Terbanyak</CTableHeaderCell>
                  </CTableRow>
                </CTableHead>
                <CTableBody>
                  {filteredUplineKeys.map((key) => {
                    const g = groupedByUpline[key];
                    const topType = Object.entries(g.typeCount)
                      .sort((a, b) => b[1] - a[1])
                      .map(([type, count]) => ({ type, count }))[0];
                    return (
                      <CTableRow
                        key={key}
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          setSelectedUplineKey(key);
                          setShowModalType(true);
                        }}
                      >
                        <CTableDataCell>{g.upline}</CTableDataCell>
                        <CTableDataCell className="text-center">
                          <CBadge color="primary">{g.total}</CBadge>
                        </CTableDataCell>
                        <CTableDataCell className="text-center">
                          <CBadge 
                            color="success" 
                            style={{ cursor: "pointer" }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleVisitFilterClick("visited");
                            }}
                          >
                            {g.visited}
                          </CBadge>
                        </CTableDataCell>
                        <CTableDataCell className="text-center">
                          <CBadge 
                            color="danger" 
                            style={{ cursor: "pointer" }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleVisitFilterClick("not_visited");
                            }}
                          >
                            {g.notVisited}
                          </CBadge>
                        </CTableDataCell>
                        <CTableDataCell>
                          {topType ? (
                            <span
                              style={{ cursor: "pointer" }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleTypeFilterClick(topType.type);
                              }}
                            >
                              Tipe {topType.type} ({topType.count})
                            </span>
                          ) : (
                            "-"
                          )}
                        </CTableDataCell>
                      </CTableRow>
                    );
                  })}
                </CTableBody>
                </CTable>
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
      
      <CModal visible={showModalType} onClose={() => setShowModalType(false)} alignment="center" size="xl">
        <CModalHeader onClose={() => setShowModalType(false)}>
          <CModalTitle>
            Distribusi Tipe - {selectedUplineKey ? groupedByUpline[selectedUplineKey]?.upline || selectedUplineKey : 'Unknown'}
          </CModalTitle>
        </CModalHeader>
        <CModalBody>
          {selectedUplineKey && (
            (() => {
              const g = groupedByUpline[selectedUplineKey];
              
              // Group data by visit status and type
              const visitedData = {};
              const notVisitedData = {};
              
              // Process stores for this upline
              stores.forEach(store => {
                if (store?.upline_name === selectedUplineKey) {
                  const type = store?.type || '-';
                  const isVisited = !!store?.last_visited;
                  
                  if (isVisited) {
                    visitedData[type] = (visitedData[type] || 0) + 1;
                  } else {
                    notVisitedData[type] = (notVisitedData[type] || 0) + 1;
                  }
                }
              });
              
              // Get all unique types
              const allTypes = [...new Set([...Object.keys(visitedData), ...Object.keys(notVisitedData)])];
              
              // Prepare data for visited stores
              const visitedLabels = allTypes.map(type => `Tipe ${type}`);
              const visitedValues = allTypes.map(type => visitedData[type] || 0);
              const visitedColors = ["#4BC0C0", "#36A2EB", "#FFCE56", "#FF6384", "#9966FF", "#FF9F40"];
              
              // Prepare data for not visited stores
              const notVisitedLabels = allTypes.map(type => `Tipe ${type}`);
              const notVisitedValues = allTypes.map(type => notVisitedData[type] || 0);
              const notVisitedColors = ["#A8E6CF", "#87CEEB", "#FFD93D", "#FFB6C1", "#DDA0DD", "#FFB366"];
              
              return allTypes.length ? (
                <div>
                  {/* Header dengan informasi upline */}
                  {/* <div className="text-center mb-4">
                    <h4 className="text-primary mb-2">
                      <i className="cil-user me-2"></i>
                      {g?.upline || 'Unknown Upline'}
                    </h4>
                    <div className="row justify-content-center">
                      <div className="col-auto">
                        <CBadge color="info" className="me-2">
                          Total Toko: {g?.total || 0}
                        </CBadge>
                      </div>
                      <div className="col-auto">
                        <CBadge color="success" className="me-2">
                          Terkunjungi: {g?.visited || 0}
                        </CBadge>
                      </div>
                      <div className="col-auto">
                        <CBadge color="warning">
                          Belum: {g?.notVisited || 0}
                        </CBadge>
                      </div>
                    </div>
                  </div> */}

                  <CRow className="g-4">
                    {/* Chart Terkunjungi */}
                    <CCol md={6}>
                      <div className="text-center mb-3">
                        <h5 className="text-success">
                          <i className="cil-check-circle me-2"></i>
                          Toko Terkunjungi
                        </h5>
                        <small className="text-muted">
                          Total: {visitedValues.reduce((sum, val) => sum + val, 0)} toko
                        </small>
                      </div>
                      <div style={{ height: '300px', position: 'relative' }}>
                        <CChartDoughnut
                          data={{
                            labels: visitedLabels,
                            datasets: [
                              { 
                                backgroundColor: visitedColors, 
                                data: visitedValues,
                                borderWidth: 2,
                                borderColor: '#fff'
                              },
                            ],
                          }}
                          options={{ 
                            maintainAspectRatio: false,
                            responsive: true,
                            plugins: {
                              legend: {
                                position: 'bottom',
                                labels: {
                                  usePointStyle: true,
                                  padding: 15,
                                  font: {
                                    size: 12
                                  }
                                }
                              }
                            },
                            onClick: (evt, elements) => {
                              if (elements && elements.length > 0) {
                                const index = elements[0].index;
                                const clickedType = allTypes[index];
                                handleDonutClick(selectedUplineKey, clickedType, 'visited');
                              }
                            }
                          }}
                        />
                      </div>
                    </CCol>
                    
                    {/* Chart Belum Terkunjungi */}
                    <CCol md={6}>
                      <div className="text-center mb-3">
                        <h5 className="text-warning">
                          <i className="cil-clock me-2"></i>
                          Toko Belum Terkunjungi
                        </h5>
                        <small className="text-muted">
                          Total: {notVisitedValues.reduce((sum, val) => sum + val, 0)} toko
                        </small>
                      </div>
                      <div style={{ height: '300px', position: 'relative' }}>
                        <CChartDoughnut
                          data={{
                            labels: notVisitedLabels,
                            datasets: [
                              { 
                                backgroundColor: notVisitedColors, 
                                data: notVisitedValues,
                                borderWidth: 2,
                                borderColor: '#fff'
                              },
                            ],
                          }}
                          options={{ 
                            maintainAspectRatio: false,
                            responsive: true,
                            plugins: {
                              legend: {
                                position: 'bottom',
                                labels: {
                                  usePointStyle: true,
                                  padding: 15,
                                  font: {
                                    size: 12
                                  }
                                }
                              }
                            },
                            onClick: (evt, elements) => {
                              if (elements && elements.length > 0) {
                                const index = elements[0].index;
                                const clickedType = allTypes[index];
                                handleDonutClick(selectedUplineKey, clickedType, 'not_visited');
                              }
                            }
                          }}
                        />
                      </div>
                    </CCol>
                  </CRow>
                  
                  {/* Summary Cards */}
                  {/* <CRow className="mt-4">
                    <CCol md={6}>
                      <CCard className="border-success">
                        <CCardBody className="text-center">
                          <h6 className="text-success">Terkunjungi</h6>
                          <h4 className="text-success">
                            {visitedValues.reduce((sum, val) => sum + val, 0)}
                          </h4>
                          <small className="text-muted">toko</small>
                        </CCardBody>
                      </CCard>
                    </CCol>
                    <CCol md={6}>
                      <CCard className="border-warning">
                        <CCardBody className="text-center">
                          <h6 className="text-warning">Belum Terkunjungi</h6>
                          <h4 className="text-warning">
                            {notVisitedValues.reduce((sum, val) => sum + val, 0)}
                          </h4>
                          <small className="text-muted">toko</small>
                        </CCardBody>
                      </CCard>
                    </CCol>
                  </CRow> */}
                </div>
              ) : (
                <div className="text-muted text-center py-4">
                  <i className="cil-info me-2"></i>
                  Tidak ada data tipe untuk upline ini
                </div>
              );
            })()
          )}
        </CModalBody>
      </CModal>
    </div>
  );
}

export default StoresPerUpline;

